// Standard page container: one horizontal-centering wrapper with the shared
// `px-4 py-6` gutters and a width variant per page density. Per-page max-widths
// are intentional (data-dense pages get more room, focused flows stay narrow),
// so the variant keeps them as one system instead of ad-hoc numbers per file.

import type { ComponentProps } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const pageContainerVariants = cva('mx-auto w-full px-4 py-6', {
  variants: {
    width: {
      /** Focused single-column flows (Lists, Agenda). */
      narrow: 'max-w-3xl',
      /** Launcher / light content (Hub). */
      default: 'max-w-5xl',
      /** Finance pages (Expenses, Bills, Reports, Accounts, Categories, Investments). */
      wide: 'max-w-6xl',
      /** Data-dense dashboard. */
      xl: 'max-w-7xl',
    },
  },
  defaultVariants: {
    width: 'wide',
  },
});

type PageContainerProps = ComponentProps<'div'> &
  VariantProps<typeof pageContainerVariants>;

export function PageContainer({ width, className, ...props }: PageContainerProps) {
  return <div className={cn(pageContainerVariants({ width }), className)} {...props} />;
}
